import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { logActivity } from '@/lib/activityLog';
import type { InventoryMovement } from '@/lib/types';

const PAGE_SIZE = 30;

export function useMovements(branchId?: string | null) {
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pageRef = useRef(0);

  const runQuery = useCallback(
    async (reset: boolean) => {
      if (reset) {
        setLoading(true);
        pageRef.current = 0;
      } else {
        setLoadingMore(true);
      }
      setError(null);
      const from = pageRef.current * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      try {
        let query = supabase
          .from('inventory_movements')
          .select('*, product:products(name, image_url), branch:branches(name)', { count: 'exact' })
          .range(from, to)
          .order('created_at', { ascending: false });
        if (branchId) {
          query = query.eq('branch_id', branchId);
        }
        const { data, count, error: e } = await query;
        if (e) throw e;
        setTotal(count ?? 0);
        setMovements((prev) =>
          reset
            ? ((data as InventoryMovement[]) ?? [])
            : [...prev, ...((data as InventoryMovement[]) ?? [])]
        );
        pageRef.current += 1;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'No se pudieron cargar los movimientos');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [branchId]
  );

  useEffect(() => {
    runQuery(true);
  }, [runQuery]);

  const loadMore = useCallback(() => runQuery(false), [runQuery]);
  const hasMore = movements.length < total;

  return { movements, total, loading, loadingMore, error, hasMore, loadMore, reload: () => runQuery(true) };
}

export interface AdjustPayload {
  product_id: string;
  quantity: number;
  type: string;
  reason?: string;
  note?: string;
  branch_id?: string | null;
}

export async function adjustStock(payload: AdjustPayload): Promise<void> {
  const { error } = await supabase.rpc('adjust_inventory', { payload });
  if (error) {
    throw new Error(error.message);
  }
  void logActivity({
    module: 'inventory',
    action: 'inventory_adjusted',
    description: `Inventario (${payload.type}): ${payload.quantity > 0 ? '+' : ''}${payload.quantity}`,
    targetType: 'product',
    targetId: payload.product_id,
    metadata: {
      type: payload.type,
      quantity: payload.quantity,
      reason: payload.reason ?? null,
    },
    branchId: payload.branch_id ?? null,
  });
}